import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Typography,
  FormControlLabel,
  Checkbox,
  Grid,
  FormHelperText,
  Alert
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Person as PersonIcon,
  Phone as PhoneIcon,
  Store as StoreIcon
} from '@mui/icons-material';
import Swal from 'sweetalert2';
import { useTranslation } from 'react-i18next';
import { fetchAdmins, deleteAdmin, updateAdmin } from '../adminsApi';
import { fetchBranches } from '../branchesApi';

const Admins = () => {
  const { t } = useTranslation();
  const [admins, setAdmins] = useState([]);
  const [branches, setBranches] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit Modal State
  const [editOpen, setEditOpen] = useState(false);
  const [currentAdmin, setCurrentAdmin] = useState({
    Id: '',
    Name: '',
    Number: '',
    BranchId: '',
    Permission: false
  });

  useEffect(() => {
    loadAdmins();
    loadBranches();
  }, []);

  const loadAdmins = async () => {
    setLoading(true);
    try {
      const data = await fetchAdmins();
      setAdmins(data);
    } catch (error) {
      Swal.fire(t('error'), 'Error loading admins', 'error');
    } finally {
      setLoading(false);
    }
  };

  const loadBranches = async () => {
    try {
      const data = await fetchBranches();
      setBranches(data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  const handleDelete = (id) => {
    Swal.fire({
      title: t('areYouSure'),
      text: t('revertWarning'),
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: t('yesDeleteIt'),
      cancelButtonText: t('cancel')
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await deleteAdmin(id);
          Swal.fire(t('deleted'), 'Admin deleted', 'success');
          loadAdmins();
        } catch (error) {
          Swal.fire(t('error'), 'Delete failed', 'error');
        }
      }
    });
  };

  const handleEditClick = (admin) => {
    setCurrentAdmin({
      Id: admin.Id,
      Name: admin.Name,
      Number: admin.Number,
      BranchId: admin.BranchId,
      Permission: admin.Permission
    });
    setEditOpen(true);
  };

  const handleEditSave = async () => {
    try {
      const payload = {
        Name: currentAdmin.Name,
        Number: currentAdmin.Number,
        BranchId: currentAdmin.BranchId,
        Permission: currentAdmin.Permission
      };
      await updateAdmin(currentAdmin.Id, payload);
      setEditOpen(false);
      Swal.fire(t('success'), 'Admin updated', 'success');
      loadAdmins();
    } catch (error) {
      Swal.fire(t('error'), 'Update failed', 'error');
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Card>
        <CardHeader
          title={t('manageAdmins') || 'Manage Admins'}
          action={
            <Button
              variant="contained"
              color="primary"
              startIcon={<RefreshIcon />}
              onClick={loadAdmins}
              sx={{ borderRadius: 50 }}
            >
              {t('refreshData') || 'Refresh'}
            </Button>
          }
          sx={{ bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}
        />
        <CardContent>
          <TableContainer component={Paper} elevation={0}>
            <Table sx={{ minWidth: 650 }} aria-label="admins table">
              <TableHead sx={{ bgcolor: 'grey.100' }}>
                <TableRow>
                  <TableCell>#</TableCell>
                  <TableCell>{t('nameLabel') || 'Name'}</TableCell>
                  <TableCell>{t('phoneNumberLabel') || 'Phone Number'}</TableCell>
                  <TableCell>{t('branchLabel') || 'Branch'}</TableCell>
                  <TableCell>{t('permissionLabel') || 'Permission'}</TableCell>
                  <TableCell align="center">{t('actions') || 'Actions'}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {admins.map((admin, idx) => (
                  <TableRow key={admin.Id}>
                    <TableCell>{idx + 1}</TableCell>
                    <TableCell>{admin.Name}</TableCell>
                    <TableCell>{admin.Number}</TableCell>
                    <TableCell>{branches.find(b => b.Id === admin.BranchId)?.Name || admin.BranchId}</TableCell>
                    <TableCell>{admin.Permission ? t('yes') : t('no')}</TableCell>
                    <TableCell align="center">
                      <IconButton color="primary" onClick={() => handleEditClick(admin)}>
                        <EditIcon />
                      </IconButton>
                      <IconButton color="error" onClick={() => handleDelete(admin.Id)}>
                        <DeleteIcon />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </CardContent>
      </Card>
      <Dialog open={editOpen} onClose={() => setEditOpen(false)}>
        <DialogTitle>{t('editAdmin') || 'Edit Admin'}</DialogTitle>
        <DialogContent>
          <TextField
            margin="dense"
            label={t('nameLabel') || 'Name'}
            name="Name"
            value={currentAdmin.Name}
            onChange={e => setCurrentAdmin({ ...currentAdmin, Name: e.target.value })}
            fullWidth
          />
          <TextField
            margin="dense"
            label={t('phoneNumberLabel') || 'Phone Number'}
            name="Number"
            value={currentAdmin.Number}
            onChange={e => setCurrentAdmin({ ...currentAdmin, Number: e.target.value })}
            fullWidth
          />
          <FormControl fullWidth margin="dense">
            <InputLabel>{t('branchLabel') || 'Branch'}</InputLabel>
            <Select
              name="BranchId"
              value={currentAdmin.BranchId}
              onChange={e => setCurrentAdmin({ ...currentAdmin, BranchId: e.target.value })}
            >
              {branches.map(branch => (
                <MenuItem key={branch.Id} value={branch.Id}>{branch.Name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <FormControlLabel
            control={
              <Checkbox
                checked={currentAdmin.Permission}
                onChange={e => setCurrentAdmin({ ...currentAdmin, Permission: e.target.checked })}
                name="Permission"
              />
            }
            label={t('hasPermissionLabel') || 'Has Permission'}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEditSave} color="primary" variant="contained">
            {t('save') || 'Save'}
          </Button>
          <Button onClick={() => setEditOpen(false)} color="secondary" variant="contained">
            {t('cancel') || 'Cancel'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Admins;
